import { FunctionComponent, useEffect, useState } from "react";
import Table from "../../Components/Table/Table";
import CustomToolbar from "../../Components/Table/components/CustomToolbar";
import ButtonField from "../../Components/Button/ButtonField";
import { Columns } from "../../Components/Table/ITable";
import { GridRenderCellParams } from "@mui/x-data-grid";
import IconButtonField from "../../Components/Button/IconButtonField";
import { SvgIcon } from "@mui/material";
import { ReactComponent as Delete } from "../../Assets/Images/delete.svg";
import { ReactComponent as Edit } from "../../Assets/Images/edit.svg";
import { ReactComponent as RefreshButton } from "../../Assets/Images/RefreshButton.svg";
import DeleteDialog from "../../Components/DeleteDialog/DeleteDialog";
import TableSearch from "../../Components/Table/components/TableSearch";
import Dialog from "../../Components/Dialog/Dialog";
import { Box, TextField, FormControlLabel, Switch } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { listCourseCategory } from "../../Redux/Slices/CourseCategory/ListCourseCategorySlice";
import {
  addCourseCategory,
  resetaddCourseCategory,
} from "../../Redux/Slices/CourseCategory/AddCourseCategorySlice";
import { ICourseCategory } from "./ICourseCategory";
import ResponseDialog from "../../Components/ResponseDialog/ResponseDialog";

interface CourseCategoryProps { }

const CourseCategory: FunctionComponent<CourseCategoryProps> = () => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleteDialogData, setDeleteData] = useState<{ id?: string | number; name?: string }>({});

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [formData, setFormData] = useState<ICourseCategory>({
    name: "",
    description: "",
    course_category_id: "",
    status: true,
    is_delete: false,
  });

  const dispatch = useAppDispatch();
  const { data: listData, isLoading } = useAppSelector((state) => state.ListCourseCategorySlice);
  // Single slice handles add / edit / delete
  const { data: addResponse } = useAppSelector((state) => state.AddCourseCategorySlice);

  const categories = Array.isArray(listData?.data)
    ? listData?.data?.map((c: any, index: number) => ({
      ...c,
      index: index + 1,
    }))
    : [];

  useEffect(() => {
    dispatch(listCourseCategory());
  }, [dispatch]);

  // Refresh list on any successful operation (add / edit / delete all use addResponse)
  useEffect(() => {
    if (addResponse?.status === true) {
      dispatch(listCourseCategory());
    }
  }, [addResponse, dispatch]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      dispatch(resetaddCourseCategory());
    };
  }, [dispatch]);

  const handleOpenDialog = (data?: any) => {
    if (data) {
      setIsEdit(true);
      setFormData({
        id: data.id,
        name: data.name,
        description: data.description ?? "",
        course_category_id: data.course_category_id || "",
        status: data.status,
        is_delete: false,
      });
    } else {
      setIsEdit(false);
      setFormData({
        name: "",
        description: "",
        course_category_id: "",
        status: true,
        is_delete: false,
      });
    }
    setDialogOpen(true);
  };

  // Add: no id | Edit: has id, is_delete: false
  const handleSave = () => {
    dispatch(addCourseCategory(formData));
  };

  const handleDelete = () => {
    if (deleteDialogData) {
      dispatch(
        addCourseCategory({
          ...deleteDialogData,
          is_delete: true,
        })
      );
    }
    setDeleteDialogOpen(false);
  };

  const columns: Columns = [
    {
      field: "index",
      renderHeader: () => <b>S.No</b>,
      width: 90,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "name",
      renderHeader: () => <b>Category Name</b>,
      flex: 1,
      type: "string",
      headerAlign: "left",
    },
    {
      field: "description",
      renderHeader: () => <b>Description</b>,
      flex: 1.5,
      type: "string",
      headerAlign: "left",
    },
    {
      field: "status",
      renderHeader: () => <b>Status</b>,
      width: 120,
      type: "string",
      headerAlign: "left",
      cellClassName: (params) =>
        `activename ${params.row.status ? "active" : "inactive"}`,
      valueFormatter: (params) =>
        params.value === true ? "Active" : "Inactive",
    },
    {
      field: "action",
      renderHeader: () => <b>Action</b>,
      width: 150,
      type: "actions",
      renderCell: (params: GridRenderCellParams<any>) => {
        return (
          <>
            <IconButtonField
              onClick={() => {
                handleOpenDialog(params.row);
              }}
            >
              <SvgIcon sx={{ fill: "none" }} component={Edit} inheritViewBox />
            </IconButtonField>
            <IconButtonField
              onClick={() => {
                setDeleteData({ ...params.row, id: params.id });
                setDeleteDialogOpen(true);
              }}
            >
              <SvgIcon component={Delete} inheritViewBox />
            </IconButtonField>
          </>
        );
      },
    },
  ];

  return (
    <>
      <Table rows={categories || []} columns={columns} exportFileName="CourseCategories">
        <CustomToolbar>
          <TableSearch placeholder="Search Category..." />
          <Box sx={{ flexGrow: 1 }} />
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <ButtonField
              className="tabel_add_new_button"
              onClick={() => handleOpenDialog()}
              toolTip="Add new category"
            >
              Add new
            </ButtonField>
            <IconButtonField
              className="refresh_btn"
              toolTip="Refresh"
              onClick={() => dispatch(listCourseCategory())}
            >
              <SvgIcon
                className="refresh_svg_icon"
                component={RefreshButton}
                inheritViewBox
              />
            </IconButtonField>
          </div>
        </CustomToolbar>
      </Table>

      {/* Add / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        setOpen={setDialogOpen}
        maxWidth="sm"
        fullWidth
        Dialog_Title={`${isEdit ? "Edit" : "Add"} Course Category`}
        Dialog_Content={
          <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2, pb: 2 }}>
            <TextField
              label="Category Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              variant="outlined"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={4}
              variant="outlined"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                  color="primary"
                />
              }
              label={formData.status ? "Status: Active" : "Status: Inactive"}
            />
          </Box>
        }
        Dialog_Actions={
          <>
            <ButtonField
              className="form_cancel_button"
              onClick={() => setDialogOpen(false)}
            >
              Cancel
            </ButtonField>
            <ButtonField
              className="form_save_button"
              onClick={handleSave}
            >
              {isEdit ? "Update" : "Save"}
            </ButtonField>
          </>
        }
      />

      {/* Single response dialog for add / edit */}
      <ResponseDialog response={addResponse} parentDialog={setDialogOpen} />

      {/* Delete dialog — no response prop needed since delete uses addResponse */}
      <DeleteDialog
        deleteableName={deleteDialogData.name ?? ""}
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        deleteFunction={handleDelete}
      />
    </>
  );
};

export default CourseCategory;
